import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "@store";
import type { AxiosError } from "axios";
import { StudentLogModel, StudentLogQuery } from "@/types/student-log.model";
import { getAllStudentLogsService } from "@/service/student-log.service";

interface StudentLogState {
  studentLogs: StudentLogModel[];
  loading: boolean;
  error: string | null;
  totalItems: number;
  noStudentLogs: boolean;
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    pageSize: number;
  };
}

// Initial state
const initialState: StudentLogState = {
  studentLogs: [],
  loading: false,
  error: null,
  totalItems: 0,
  noStudentLogs: true,
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    pageSize: 10,
  },
};

// Service functions are now imported from the service provider

// Async thunks
export const fetchStudentLogs = createAsyncThunk(
  "studentLog/fetchAll",
  async (query: StudentLogQuery, { dispatch, getState }) => {
    try {
      dispatch(setLoading(true));

      // const state = getState() as RootState;

      // const { school_id, teacher_id } = state.auth.user || {};

      // if (school_id) {
      //   query.schoolId = school_id;
      // }

      // if (teacher_id) {
      //   query.teacherId = teacher_id;
      // }

      const {
        data: { data, pageInfo },
      } = await getAllStudentLogsService(query);
      dispatch(setLoading(false));

      dispatch(setNoStudentLogs(false));

      return {
        studentLogs: data,
        // Handle different API response formats for pagination
        pagination: {
          totalItems: pageInfo?.total_count as number,
          totalPages: pageInfo?.total_pages as number,
          currentPage: pageInfo?.current_page as number,
          pageSize: pageInfo?.per_page as number,
        },
      };
    } catch (error: any) {
      if (error.response?.status === 404) {
        dispatch(setNoStudentLogs(true));
        throw error;
      }

      const axiosError = error as AxiosError<{ message: string }>;
      const message =
        axiosError.response?.data?.message || "Failed to fetch student logs";
      dispatch(setError(message));
      throw error;
    }
  },
  {
    condition: (query, { getState }) => {
      const state = getState() as RootState;
      // Only allow this action to be executed if we're not already loading
      // This will cancel any pending requests when a new one is dispatched
      return !state.studentLog.loading;
    },
    // This will automatically cancel any pending thunk with the same action type
    dispatchConditionRejection: true,
  }
);

// Create the slice
const studentLogSlice = createSlice({
  name: "studentLog",
  initialState,
  reducers: {
    setNoStudentLogs: (state, action: PayloadAction<boolean>) => {
      state.noStudentLogs = action.payload;
    },
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },
    clearError(state) {
      state.error = null;
    },
    resetAllState() {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchStudentLogs.fulfilled, (state, action) => {
        state.studentLogs = action.payload.studentLogs;
        state.pagination = action.payload.pagination;
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchStudentLogs.rejected, (state) => {
        state.loading = false;
      });
  },
});

// Export actions
export const {
  setNoStudentLogs,
  setLoading,
  setError,
  clearError,
  resetAllState,
} = studentLogSlice.actions;

// Selectors
export const selectStudentLogs = (state: RootState) =>
  state.studentLog.studentLogs;
export const selectStudentLogLoading = (state: RootState) =>
  state.studentLog.loading;
export const selectStudentLogError = (state: RootState) =>
  state.studentLog.error;
export const selectTotalItems = (state: RootState) =>
  state.studentLog.pagination.totalItems;
export const selectNoStudentLogs = (state: RootState) =>
  state.studentLog.noStudentLogs;

export default studentLogSlice.reducer;
